// / — protected home dashboard. Step 10 fills in:
//   - cross-pack streak (with idle / active / fading copy)
//   - per-pack progress card with XP + items-unlocked progress bar
//
// Streak is pulled from /api/progress. Mutations in PracticePage invalidate
// the progress query, so navigating back to '/' shows fresh numbers.

import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { PACKS, type PackId, type PackProgress, type ProgressResponse } from '@skena/schema'
import { useAuthStore } from '../auth/store.ts'
import { useLogout } from '../auth/hooks.ts'
import { fetchProgress, PROGRESS_QUERY_KEY } from '../practice/progressApi.ts'
import { getDeviceLocalDate } from '../practice/localDate.ts'

// Cover illustration per pack — until per-pack hero images land, the most
// iconic colored item stands in. PACKS is a hardcoded const (v0.1 N=2), so
// keeping this map alongside it is consistent.
const PACK_COVER: Record<PackId, string> = {
  joseph: '/assets/packs/joseph/items/coat-colored.jpg',
  nativity: '/assets/packs/nativity/items/angel-colored.jpg',
}

export function HomePage() {
  const user = useAuthStore((s) => s.user)
  const logout = useLogout()
  const progressQuery = useQuery<ProgressResponse>({
    queryKey: PROGRESS_QUERY_KEY,
    queryFn: fetchProgress,
    staleTime: 30_000,
  })

  const progress = progressQuery.data
  const today = getDeviceLocalDate()

  return (
    <main className="mx-auto flex min-h-full max-w-2xl flex-col gap-8 p-8">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-sm text-[var(--color-muted)]">嗨，</p>
          <h1 className="font-[family-name:var(--font-display)] text-3xl text-[var(--color-fg)]">
            {user?.displayName ?? '...'}
          </h1>
        </div>
        <button
          onClick={() => logout.mutate()}
          className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
        >
          登出
        </button>
      </header>

      <StreakHero progress={progress} today={today} />

      <section>
        <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-muted)]">
          <div className="h-px flex-1 bg-[var(--color-decoration)] opacity-40" />
          <span className="tracking-[0.2em]">挑一個故事</span>
          <div className="h-px flex-1 bg-[var(--color-decoration)] opacity-40" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Object.values(PACKS).map((pack) => {
            const packProgress = progress?.packs.find((p) => p.packId === pack.id)
            return <PackCard key={pack.id} packId={pack.id} packProgress={packProgress} />
          })}
        </div>
      </section>
    </main>
  )
}

// ---------- Streak hero ----------

function StreakHero({ progress, today }: { progress: ProgressResponse | undefined; today: string }) {
  if (!progress) {
    return (
      <section
        className="skena-frame relative rounded-[var(--radius-soft)] border border-[var(--color-decoration)]/30 bg-white/70 p-5 shadow-[var(--shadow-hero)]"
        aria-busy="true"
      >
        <p className="relative text-sm text-[var(--color-muted)]">載入中…</p>
      </section>
    )
  }

  const { currentStreak, longestStreak, lastActivityDate } = progress
  const tone = streakTone({ currentStreak, lastActivityDate, today })

  return (
    <section
      className={`skena-frame relative rounded-[var(--radius-soft)] border bg-white/70 p-5 shadow-[var(--shadow-hero)] transition-colors ${
        tone === 'active'
          ? 'border-amber-400/50'
          : 'border-[var(--color-decoration)]/30'
      }`}
    >
      <div className="relative flex items-baseline justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs tracking-[0.18em] text-[var(--color-muted)] uppercase">
            <span aria-hidden="true" className="text-[var(--color-decoration)]">✦</span>
            <span>連續學習</span>
          </p>
          <div className="mt-1 flex items-baseline gap-2">
            <span aria-hidden="true" className="text-2xl">
              {tone === 'idle' ? '✨' : '🔥'}
            </span>
            <RollingNumber
              value={currentStreak}
              className="font-[family-name:var(--font-display)] text-5xl font-bold text-[var(--color-fg)]"
            />
            <span className="text-base text-[var(--color-muted)]">天</span>
          </div>
        </div>
        {longestStreak > currentStreak && longestStreak > 0 ? (
          <p className="text-xs text-[var(--color-muted)]">最高 {longestStreak} 天</p>
        ) : null}
      </div>
      <p className="relative mt-3 text-sm italic text-[var(--color-muted)]">{streakCopy(tone)}</p>
    </section>
  )
}

type StreakTone = 'idle' | 'active' | 'fading'

function streakTone(args: {
  currentStreak: number
  lastActivityDate: string | null
  today: string
}): StreakTone {
  const { currentStreak, lastActivityDate, today } = args
  if (lastActivityDate === null || currentStreak === 0) return 'idle'
  if (lastActivityDate === today) return 'active'
  // Active streak but the user hasn't shown up today yet — gentle nudge.
  if (calendarDayDiff(lastActivityDate, today) === 1) return 'active'
  return 'fading'
}

function calendarDayDiff(a: string, b: string): number {
  return Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000,
  )
}

function streakCopy(tone: StreakTone): string {
  if (tone === 'idle') return '今天答對第一題就開始了'
  if (tone === 'active') return '今天繼續就保持下去了'
  return '再回來看看吧，今天答一題就接回來'
}

// ---------- Rolling number (0.4s) ----------

function RollingNumber({ value, className }: { value: number; className?: string }) {
  const motionValue = useMotionValue(value)
  const spring = useSpring(motionValue, { duration: 400, bounce: 0 })
  const display = useTransform(spring, (v) => Math.round(v).toString())
  const prevRef = useRef(value)

  useEffect(() => {
    if (prevRef.current !== value) {
      motionValue.set(value)
      prevRef.current = value
    }
  }, [value, motionValue])

  // Initial render: snap to value (no roll on first paint).
  useEffect(() => {
    motionValue.jump(value)
    spring.jump(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <motion.span className={className}>{display}</motion.span>
}

// ---------- Pack card ----------

function PackCard({
  packId,
  packProgress,
}: {
  packId: PackId
  packProgress: PackProgress | undefined
}) {
  const pack = PACKS[packId]
  const cardBgClass =
    packId === 'joseph'
      ? 'bg-[var(--color-joseph-bg)]'
      : 'bg-[var(--color-nativity-bg)]'
  const fillClass =
    packId === 'joseph' ? 'bg-[var(--color-joseph-primary)]' : 'bg-[var(--color-nativity-primary)]'

  const itemsUnlocked = packProgress?.itemsUnlocked ?? 0
  const totalItems = packProgress?.totalItems ?? pack.itemIds.length
  const totalXp = packProgress?.totalXp ?? 0
  const pct = totalItems > 0 ? Math.min(1, itemsUnlocked / totalItems) : 0

  return (
    <Link
      to={`/pack/${pack.id}`}
      className={`skena-frame relative block overflow-hidden rounded-[var(--radius-soft)] shadow-[var(--shadow-card)] transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-2 focus-visible:outline-offset-2 ${cardBgClass}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-black/5">
        <img
          src={PACK_COVER[packId]}
          alt={pack.nameZh}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="relative p-4">
        <div className="mb-1 flex items-baseline gap-1.5 text-xs">
          <span aria-hidden="true" className="text-[var(--color-decoration)]">❦</span>
          <span className="text-[var(--color-muted)]">{pack.scriptureRange}</span>
        </div>
        <h3
          className="font-[family-name:var(--font-display)] text-lg leading-tight font-bold text-[var(--color-fg)]"
          style={{ letterSpacing: '0.01em' }}
        >
          {pack.nameZh}
        </h3>
        <p className="mt-0.5 text-xs italic text-[var(--color-muted)]">{pack.nameEn}</p>

        <div className="mt-3">
          <div className="flex items-baseline justify-between text-xs text-[var(--color-muted)]">
            <span>
              {itemsUnlocked} / {totalItems} 蒐集
            </span>
            <span>{totalXp} XP</span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[color-mix(in_oklab,var(--color-fg)_8%,transparent)]">
            <motion.div
              className={`h-full ${fillClass}`}
              initial={{ width: 0 }}
              animate={{ width: `${pct * 100}%` }}
              transition={{ type: 'spring', stiffness: 220, damping: 28 }}
            />
          </div>
        </div>
      </div>
    </Link>
  )
}

